import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { EStep } from '@domain/enums/onboarding/EStep';
import { EType } from '@domain/enums/onboarding/EType';
import { IParams } from '@domain/interfaces/IParams';

import { useOnboarding } from '@helpers/hooks/pages/onboarding/useOnboarding';
import { useConfig } from '@helpers/hooks/useConfig';
import { useInfoProduct } from '@helpers/hooks/pages/onboarding/useInfoProduct';

import { ADS_PROVIDERS } from '@constants/common/integrations';

import onboardingStepService from '@services/pages/onboarding/onboardingStep';

const Marketing: React.FC = () => {
  const history = useHistory();
  const { storeAliasId } = useParams<IParams>();
  const { completeStep, handleStoreBonus } = useOnboarding();
  const { analytics, user } = useConfig();

  const {
    currentMarketingToIntegrate,
    handleNextMarketingToIntegrate,
    selectedMarketings,
    hasSelectedStoreIntegrations,
  } = useInfoProduct();

  const { onboardingSteps } = onboardingStepService.getOnboardingSteps({ storeAliasId });

  const getMarketingContent = React.useCallback(
    (integration: typeof ADS_PROVIDERS[number] | undefined) => {
      if (integration) {
        const Content = integration.onboardingContent;

        return <Content callback={handleNextMarketingToIntegrate} />;
      }

      return <></>;
    },
    [handleNextMarketingToIntegrate],
  );

  React.useEffect(() => {
    if (
      !selectedMarketings.length &&
      !currentMarketingToIntegrate &&
      hasSelectedStoreIntegrations
    ) {
      if (onboardingSteps) {
        const foundOnboardingStep = onboardingSteps.find(
          step =>
            step.configuration_step === EStep.STORE_INTEGRATIONS &&
            step.type === EType.INFO_PRODUCT,
        );

        if (foundOnboardingStep) {
          completeStep(foundOnboardingStep.id);
        }
      }

      analytics?.track(
        'Onboarding Integrations Done',
        {
          email: user?.email,
        },
        { context: { groupId: storeAliasId } },
      );
      analytics?.trackHubSpot(storeAliasId, { is_onboarding_integrations_done: true });

      handleStoreBonus();
    }

    if (
      !selectedMarketings.length &&
      !currentMarketingToIntegrate &&
      !hasSelectedStoreIntegrations
    ) {
      history.push(`/onboarding/${storeAliasId}/info-product/integrations`);
    }

    if (!currentMarketingToIntegrate) {
      handleNextMarketingToIntegrate();
    }
  }, [
    selectedMarketings,
    currentMarketingToIntegrate,
    history,
    storeAliasId,
    handleNextMarketingToIntegrate,
    hasSelectedStoreIntegrations,
    completeStep,
    onboardingSteps,
    analytics,
    user,
    handleStoreBonus,
  ]);

  const foundMarketing = ADS_PROVIDERS.find(
    integration => integration.identifier === currentMarketingToIntegrate,
  );

  return <>{getMarketingContent(foundMarketing)}</>;
};

export default Marketing;
